import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Button,
  FormHelperText,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export type ShareOption = {
  value: string;
  label: string;
};

interface ShareLinkDialogProps {
  open: boolean;
  title: string;
  shareType: string;
  onShareTypeChange: (value: string) => void;
  shareOptions: ShareOption[];
  nameLabel?: string;
  nameValue?: string;
  onNameChange?: (value: string) => void;
  nameRequired?: boolean;
  namePlaceholder?: string;
  password?: string;
  onPasswordChange?: (value: string) => void;
  showPasswordFor?: string[];
  generatedLink?: string;
  onCopy?: () => void;
  onGenerate: () => void;
  error?: string;
  creating?: boolean;
  helperText?: string;
  actionLabel?: string;
  onClose: () => void;
  children?: React.ReactNode;
}

const ShareLinkDialog: React.FC<ShareLinkDialogProps> = ({
  open,
  title,
  shareType,
  onShareTypeChange,
  shareOptions,
  nameLabel,
  nameValue,
  onNameChange,
  nameRequired = false,
  namePlaceholder,
  password,
  onPasswordChange,
  showPasswordFor = ["password"],
  generatedLink,
  onCopy,
  onGenerate,
  error,
  creating,
  helperText,
  actionLabel = "Create Link",
  onClose,
  children,
}) => {
  const handleCopy = async () => {
    if (onCopy) {
      onCopy();
      return;
    }
    if (generatedLink) {
      await navigator.clipboard.writeText(generatedLink);
    }
  };

  const showPassword =
    Boolean(onPasswordChange) && showPasswordFor.includes(shareType);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {typeof onNameChange === "function" && (
            <TextField
              label={nameLabel || "Name"}
              size="small"
              value={nameValue || ""}
              onChange={(e) => onNameChange(e.target.value)}
              required={nameRequired}
              placeholder={namePlaceholder}
            />
          )}
          <FormControl component="fieldset">
            <FormLabel component="legend">Share Type</FormLabel>
            <RadioGroup
              row
              value={shareType}
              onChange={(e) => onShareTypeChange(e.target.value)}
              sx={{ gap: 2, flexWrap: "wrap" }}
            >
              {shareOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  label={option.label}
                  sx={{ marginRight: 0 }}
                />
              ))}
            </RadioGroup>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>

          {showPassword && (
            <TextField
              label="Password"
              type="password"
              size="small"
              value={password || ""}
              onChange={(e) => onPasswordChange?.(e.target.value)}
            />
          )}

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}

          {generatedLink && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                value={generatedLink}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <Tooltip title="Copy link">
                <IconButton onClick={handleCopy} size="small">
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
        {children}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={onGenerate} disabled={creating}>
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareLinkDialog;
